import { LL } from 'i18n/i18n.service';
import { AlertButtonRole, AlertController } from 'providers/alert-controller';

/**
 * Tell a buyer that the store's own record says a different one of our accounts
 * made this purchase, and no account we can name holds it (#5384) — usually an
 * account they have since deleted.
 *
 * One OK button: nothing on the screen can fix it, and the message says who
 * can. Shared by the Subscribe page and both coach screens, so the wording and
 * the button change in one place.
 */
export async function showOtherAccountAlertAsync(alertCtrl: AlertController): Promise<void> {
  await alertCtrl.showAlertAsync({
    title: LL.subscribe.alerts.otherAccount.title(),
    message: LL.subscribe.alerts.otherAccount.message(),
    buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: null }],
  });
}

/**
 * Tell a buyer that the store refused our request to confirm their purchase
 * (`store_request_refused`, #6239), which it does for that token every time
 * (#6243). So the message never says to try again, and points a buyer who was
 * charged at support. It never says the purchase is invalid either: the refusal
 * says nothing about whether one exists.
 *
 * One OK button, for the same reason as the other-account alert above. Shared
 * by the Subscribe page (purchase and Restore Purchases) and both coach screens.
 */
export async function showStoreRefusedAlertAsync(alertCtrl: AlertController): Promise<void> {
  await alertCtrl.showAlertAsync({
    title: LL.subscribe.alerts.storeRefused.title(),
    message: LL.subscribe.alerts.storeRefused.message(),
    buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: null }],
  });
}

/**
 * Tell a coach that the store subscription is linked to a different app account,
 * naming it when the server told us which. It reuses the personal already-linked
 * wording.
 *
 * One OK button. The Subscribe page's own version also offers to sign out, but
 * the coach screens never have. Shared by both coach screens so the two cannot
 * drift.
 */
export async function showCoachAlreadyLinkedAlertAsync(alertCtrl: AlertController, linkedEmail: string | undefined): Promise<void> {
  const email = linkedEmail || LL.subscribe.alerts.alreadyLinked.unknownAccount();
  await alertCtrl.showAlertAsync({
    title: LL.subscribe.alerts.alreadyLinked.title(),
    message: LL.subscribe.alerts.alreadyLinked.message({ email }),
    buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: null }],
  });
}
